import { ref, onMounted, onUnmounted, type Ref } from 'vue'
import type { WaitingTimeInfo } from '../types/interfaces'
import { WaitingTimeSeverity } from '../types/enums'

export const parseUtcDate = (date: string | Date): Date => {
  if (date instanceof Date) return date

  const trimmedDate = date.trim()
  if (!trimmedDate) return new Date('')

  const hasTimezone = /(Z|[+\-]\d{2}:?\d{2})$/.test(trimmedDate)
  const normalizedDate = hasTimezone ? trimmedDate : `${trimmedDate}Z`

  return new Date(normalizedDate)
}

export const useTimeFormatter = () => {
  const formatWaitingTime = (startDate: string | Date): string => {
    const start = parseUtcDate(startDate).getTime()
    const now = Date.now()
    const diffMs = now - start

    const seconds = Math.floor(diffMs / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) return `${days}d ${hours % 24}h`
    if (hours > 0) return `${hours}h ${minutes % 60}m`
    if (minutes > 0) return `${minutes}m`
    return `${seconds}s`
  }

  const formatMessageTime = (date: string | Date): string => {
    const messageDate = parseUtcDate(date)

    if (Number.isNaN(messageDate.getTime())) {
      return ''
    }

    const now = new Date()
    const diffMs = now.getTime() - messageDate.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return messageDate.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    if (diffDays === 1) return 'Ayer'
    if (diffDays < 7) {
      return messageDate.toLocaleDateString('es-ES', { weekday: 'short' })
    }

    return messageDate.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
  }

  const formatAudioDuration = (seconds: number): string => {
    const totalSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0
    const hours = Math.floor(totalSeconds / 3600)
    const mins = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const calculateWaitingTime = (timestamp: string): WaitingTimeInfo => {
    const now = new Date()
    const then = parseUtcDate(timestamp)

    if (isNaN(then.getTime())) {
      return {
        minutes: 0,
        severity: WaitingTimeSeverity.INFO,
        label: '0 min',
        color: '#2563eb',
        shouldPulse: false,
      }
    }

    const diffInMinutes = Math.max(0, Math.floor((now.getTime() - then.getTime()) / (1000 * 60)))

    let severity: WaitingTimeSeverity
    let color: string
    let shouldPulse = false

    if (diffInMinutes < 5) {
      severity = WaitingTimeSeverity.SUCCESS
      color = '#10b981'
    } else if (diffInMinutes < 15) {
      severity = WaitingTimeSeverity.INFO
      color = '#3b82f6'
    } else if (diffInMinutes < 30) {
      severity = WaitingTimeSeverity.WARN
      color = '#f59e0b'
    } else if (diffInMinutes < 60) {
      severity = WaitingTimeSeverity.DANGER
      color = '#ef4444'
      shouldPulse = true
    } else {
      severity = WaitingTimeSeverity.CRITICAL
      color = '#dc2626'
      shouldPulse = true
    }

    let label: string
    if (diffInMinutes < 60) {
      label = `${diffInMinutes} min`
    } else {
      const hours = Math.floor(diffInMinutes / 60)
      const mins = diffInMinutes % 60
      label = mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }

    return {
      minutes: diffInMinutes,
      severity,
      label,
      color,
      shouldPulse,
    }
  }

  return {
    formatWaitingTime,
    formatMessageTime,
    formatAudioDuration,
    calculateWaitingTime,
  }
}

export const useReactiveWaitingTime = (timestamp: Ref<string>) => {
  const { calculateWaitingTime } = useTimeFormatter()
  const waitingTimeInfo = ref<WaitingTimeInfo>(calculateWaitingTime(timestamp.value))

  let intervalId: number | null = null

  onMounted(() => {
    intervalId = window.setInterval(() => {
      waitingTimeInfo.value = calculateWaitingTime(timestamp.value)
    }, 30000)
  })

  onUnmounted(() => {
    if (intervalId) clearInterval(intervalId)
  })

  return { waitingTimeInfo }
}
